import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

// Define the API slice
export const api = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_URL || "https://iplunify.jagsonpal.in/api", // Ensure this is defined in your .env file
  }),
  reducerPath: "adminApi", // Key in Redux store
  tagTypes: [
    "User",
    "OverallStat",
    "ManagementStat",
    "Md",
    "Product",
    "State",
    "DocRep",
    "GetRecords",
    "getPobRecords",
    "bonevainrecord",
    "SmDayWiseRecords",
  ], // Updated to include 'GetRecords'
  endpoints: (build) => ({
    updatepassword: build.mutation({
      query: ({ EmpNo, newpassword }) => ({
        url: "/resetpassword",
        method: "POST",
        body: { EmpNo, newpassword },
      }),
      invalidatesTags: ["Users"],
    }),

    // Query to get employees for password reset
    getEmployeesForReset: build.query({
      query: () => "/getEmployeesForReset",
      providesTags: ["EmployeesForReset"],
    }),

    // Mutation to reset employee password from admin
    resetEmployeePassword: build.mutation({
      query: ({ EmpNo, newpassword }) => ({
        url: "/resetpassword",
        method: "POST",
        body: { EmpNo, newpassword },
      }),
      invalidatesTags: ["EmployeesForReset"],
    }),

    //fetch data day wise for pob daily wise

    getDayWisePobData: build.mutation({
      query: ({ month, year }) => ({
        url: "/daywisepob-report",
        method: "POST",
        body: { month, year },
      }),
      invalidatesTags: ["mrrecord"], // Invalidate cache for state data
    }),

    // Fetch hierarchy structure for POB report
    getHierarchyStructure: build.query({
      query: () => "/hierarchy-structure",
      providesTags: ["HierarchyStructure"],
    }),

    // fetch data day wise for admin page
    getDayWiseData: build.mutation({
      query: ({ month, year, product }) => ({
        url: "/daywise-report",
        method: "POST",
        body: { month, year, product },
      }),
      invalidatesTags: ["mrrecord"], // Invalidate cache for state data
    }),

    // query to get state in admin page
    getState: build.query({
      query: () => "/adminState",
      providesTags: ["State"],
    }),

    // Query to fetch a user by ID
    getUser: build.query({
      query: (id) => `general/user/${id}`,
      providesTags: (result, error, id) => [{ type: "User", id }],
    }),

    //query best scorer for navbar with product filter

    getBestScorers: build.query({
      query: ({ product } = {}) => {
        const url = product ? `/bestscorer?product=${product}` : "/bestscorer";
        return url;
      },
      providesTags: ["BestScorer"],
    }),

    // query to get emp for admin page
    getEmpDataAdmin: build.query({
      query: ({ RmMgName, product, month, year, day }) => ({
        url: "/getEmpData",
        method: "POST",
        body: { RmMgName, product, month, year, day },
      }),
      providesTags: ["empdata"],
    }),

    // query to find status of differrent empno.
    getMrStatusRecord: build.query({
      query: (EmpNo) => ({
        url: "/mrstatus",
        method: "POST",
        body: { EmpNo },
      }),
      providesTags: ["State"],
    }),

    //Query to fetch pob images in admin page
    getPobImages: build.query({
      query: ({ day, month, year } = {}) => {
        const today = new Date();
        return {
          url: "/getpobImages",
          method: "POST",
          body: {
            day: day || today.getDate(),
            month: month || today.getMonth() + 1,
            year: year || today.getFullYear(),
          },
        };
      },
      providesTags: ["bonevainrecord"],
    }),

    // Query to fetch admin page images
    getImages: build.query({
      query: ({
        page = 1,
        limit = 10,
        product = "",
        month = null,
        year = null,
        day = "",
      } = {}) => ({
        url: "/getImages",
        method: "POST",
        body: {
          page: parseInt(page) || 1,
          limit: parseInt(limit) || 10,
          product: product || null,
          month: month ? parseInt(month) : null,
          year: year ? parseInt(year) : null,
          day: day ? String(day) : null,
        },
      }),
      providesTags: ["mrrecord"],
    }),

    // Mutation to fetch admin page data with month filter
    getAdminPageData: build.mutation({
      query: ({ month, year }) => ({
        url: "/adminpagedata",
        method: "POST",
        body: { month, year },
      }),
      invalidatesTags: ["adminpagedata"],
    }),

    getpobdata: build.query({
      query: (MgCd) => ({
        url: "/getPobRecords",
        method: "POST",
        body: { MgCd },
      }),
      providesTags: ["getPobRecords"],
    }),

    // Query to fetch state data OF MR FOR AM/RM APPROVAL
    getMrData: build.query({
      query: (MgCd) => ({
        url: "/getRecords",
        method: "POST",
        body: { MgCd },
      }),
      providesTags: ["GetRecords"],
    }),

    // Query to fetch document representations based on MgCd
    getDocRep: build.query({
      query: (MgCd) => ({
        url: `/docrep/docrep/`, // Ensure backend accepts this format
        method: "POST",
        body: { MgCd },
      }),
      providesTags: ["DocRep"],
    }),

    // Mutation to update status of MrRecord

    updatepobstatus: build.mutation({
      query: ({ id, MclCode, MrMgCd, status }) => ({
        // Added 'status' here
        url: "/updatepobstatus",
        method: "POST",
        body: { id, MclCode, MrMgCd, status }, // Include 'status' in the request body
      }),
      invalidatesTags: ["getpobdata"], // Ensure cache is invalidated for fresh data
    }),

    // Mutation to update status of MrRecord
    updateStatus: build.mutation({
      query: ({ id, MrMgCd, MclCode, status }) => ({
        // Added 'status' here
        url: "/updateStatus",
        method: "POST",
        body: { id, MrMgCd, MclCode, status }, // Include 'status' in the request body
      }),
      invalidatesTags: ["GetRecords"], // Ensure cache is invalidated for fresh data
    }),

    // Mutation to update document representation
    updateDocRep: build.mutation({
      query: ({ id, ...patch }) => ({
        url: `/docrep/docrep/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "DocRep", id }],
    }),

    // upload mr status
    uploadStatus: build.query({
      query: (EmpNo) => ({
        url: "/uploadStatus",
        method: "POST",
        body: { EmpNo },
      }),
      providesTags: ["State"],
    }),

    // Mutation to update a user by ID
    updateUser: build.mutation({
      query: ({ id, ...patch }) => ({
        url: `general/user/${id}`,
        method: "PATCH",
        body: patch,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: "User", id }],
    }),

    uploadDrData: build.mutation({
      query: (doctorData) => {
        console.log("Uploading doctor data:", doctorData); // Debugging log

        return {
          url: "/uploaddrdata",
          method: "POST",
          body: JSON.stringify(doctorData), // Convert the object to JSON
          headers: {
            "Content-Type": "application/json", // This is fine to keep
            Accept: "application/json",
          },
        };
      },
      invalidatesTags: ["Docmst"],
    }),
    // upload chemist data
    uploadChemistPob: build.mutation({
      query: (formData) => {
        console.log(
          "Uploading prescription:",
          Object.fromEntries(formData.entries()),
        ); // Debugging log
        return {
          url: "/chemistdata",
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
          },
        };
      },
      invalidatesTags: ["DocRep"],
    }),

    // Mutation to upload prescription data
    uploadPrescription: build.mutation({
      query: (formData) => {
        console.log(
          "Uploading prescription:",
          Object.fromEntries(formData.entries()),
        ); // Debugging log
        return {
          url: "/mrdata",
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json",
          },
        };
      },
      invalidatesTags: ["DocRep"],
    }),

    // Mutation for user login
    loginUser: build.mutation({
      query: ({ EmpNo, password }) => ({
        url: "auth/login",
        method: "POST",
        body: { EmpNo, password },
      }),
    }),

    // Get SM Daily Wise Records (POB and Prescription) - Last 7 Days
    getSmDayWiseRecords: build.mutation({
      query: ({ SmMgCd }) => ({
        url: "/sm-daywise-records",
        method: "POST",
        body: { SmMgCd },
      }),
      invalidatesTags: ["SmDayWiseRecords"],
    }),
  }),
});

// Export hooks for usage in functional components
export const {
  useGetUserQuery,
  useUpdateUserMutation,
  useGetSalesQuery,
  useGetManagementQuery,
  useGetClientsQuery,
  useGetProductsQuery,
  useLoginUserMutation,
  useFetchStateByDateMutation,
  useUploadPrescriptionMutation,
  useGetDocRepQuery,
  useUpdateDocRepMutation,
  useGetMrDataQuery,
  useUpdateStatusMutation,
  useUploadDrDataMutation,
  useGetMrStatusRecordQuery,
  useGetBestScorersQuery,
  useUpdatepasswordMutation,
  useGetStateQuery,
  useGetAdminPageDataMutation,
  useGetImagesQuery,
  useGetDayWiseDataMutation,
  useGetEmpDataAdminQuery,
  useUploadChemistPobMutation,
  useGetpobdataQuery,
  useUpdatepobstatusMutation,
  useGetPobImagesQuery,
  useGetDayWisePobDataMutation,
  useGetHierarchyStructureQuery,
  useGetEmployeesForResetQuery,
  useResetEmployeePasswordMutation,
  useGetSmDayWiseRecordsMutation,
} = api;

